import type { PlainMessage } from '@bufbuild/protobuf';
import cn from 'classnames';
import { FC, ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  NoteTeethGroup,
  type NoteDentition,
  type NoteTeethBlock,
} from '@/shared/api/protocol-ts/model/dto_scribe_catalog_pb';
import type { NoteToothNotation } from '@/shared/api/protocol-ts/model/dto_scribe_encounter_pb';
import { ToothStatus } from '@/shared/config';
import { ToothView } from '@/shared/ui';

import { noteTeethOfferedGroups } from '@/entities/templates';

import { i18n } from '../config';
import {
  formatNoteTooth,
  getNoteTeethQuadrantNumber,
  getTeethChart,
  isNoteToothOffered,
  isToothInNoteTeethGroup,
  type TeethChartRow,
} from '../lib';

import styles from './NoteTeethPicker.module.scss';

type Props = {
  block: PlainMessage<NoteTeethBlock>;
  teeth: string[];
  groups: NoteTeethGroup[];
  notation: NoteToothNotation | undefined;
  multiple: boolean;
  // The template editor sets a value within the block's settings. The fill
  // surface takes any tooth of the block's dentition, several at once: the
  // block's settings bind the AI, not the clinician. Both offer the block's
  // groups.
  restrictToBlock: boolean;
  // A table's tooth cell holds one FDI number, never a group.
  teethOnly?: boolean;
  onChange: (teeth: string[], groups: NoteTeethGroup[]) => void;
};

// The quadrant markers around the midline, as drawn: 1 | 2 over 4 | 3.
const QUADRANTS = [
  NoteTeethGroup.NoteTeethGroupUpperRight,
  NoteTeethGroup.NoteTeethGroupUpperLeft,
  NoteTeethGroup.NoteTeethGroupLowerRight,
  NoteTeethGroup.NoteTeethGroupLowerLeft,
];

const toggle = <T,>(items: T[], item: T, multiple: boolean) => {
  if (items.includes(item)) {
    return items.filter((current) => current !== item);
  }
  return multiple ? [...items, item] : [item];
};

type TeethChartProps = {
  dentition: NoteDentition;
  offeredGroups: NoteTeethGroup[];
  isGroupPressed: (group: NoteTeethGroup) => boolean;
  renderTooth: (tooth: string, lower: boolean) => ReactNode;
  onGroup: (group: NoteTeethGroup) => void;
};

// The FDI chart of a dentition, the patient's right on the chart's left,
// with the jaw, quadrant and side shortcuts a field offers around it.
export const TeethChart: FC<TeethChartProps> = ({
  dentition,
  offeredGroups,
  isGroupPressed,
  renderTooth,
  onGroup,
}) => {
  const { formatMessage } = useIntl();
  const chart = getTeethChart(dentition);
  const offersGroup = (group: NoteTeethGroup) => offeredGroups.includes(group);
  const showsQuadrants = QUADRANTS.some(offersGroup);

  const renderRows = (rows: TeethChartRow[], lower: boolean) =>
    rows.map((row) => (
      <div key={row.right.join()} className={styles.row} role="group">
        <div className={cn(styles.half, styles.patientRight)}>
          {row.right.map((tooth) => renderTooth(tooth, lower))}
        </div>
        <div className={cn(styles.half, styles.patientLeft)}>
          {row.left.map((tooth) => renderTooth(tooth, lower))}
        </div>
      </div>
    ));

  const renderGroup = (group: NoteTeethGroup, className: string) =>
    offersGroup(group) && (
      <button
        type="button"
        className={cn('p3', styles.group, className)}
        aria-pressed={isGroupPressed(group)}
        onClick={() => onGroup(group)}
      >
        <FormattedMessage {...i18n.noteTeethGroupMessages[group]} />
      </button>
    );

  const renderQuadrant = (group: NoteTeethGroup) => {
    if (!offersGroup(group)) {
      return <span key={group} className={styles.quadrantGap} aria-hidden />;
    }
    const name = formatMessage(i18n.noteTeethGroupMessages[group]);
    return (
      <button
        key={group}
        type="button"
        className={cn('p3', styles.group, styles.quadrant)}
        aria-pressed={isGroupPressed(group)}
        aria-label={name}
        title={name}
        onClick={() => onGroup(group)}
      >
        {getNoteTeethQuadrantNumber(group, dentition)}
      </button>
    );
  };

  return (
    <div className={styles.chart}>
      {renderGroup(NoteTeethGroup.NoteTeethGroupUpper, styles.jaw)}
      {renderRows(chart.upper, false)}
      <div className={styles.bite}>
        {renderGroup(NoteTeethGroup.NoteTeethGroupRight, styles.sideOfRight)}
        {showsQuadrants && (
          <div className={styles.quadrants}>
            {QUADRANTS.map(renderQuadrant)}
          </div>
        )}
        {renderGroup(NoteTeethGroup.NoteTeethGroupLeft, styles.sideOfLeft)}
      </div>
      {renderRows(chart.lower, true)}
      {renderGroup(NoteTeethGroup.NoteTeethGroupLower, styles.jaw)}
    </div>
  );
};

// An FDI chart of the block's dentition: teeth are picked in FDI, as scribe
// stores them, and labelled in the notation the note was made with. The
// block's offered groups are the jaw, quadrant and side shortcuts shown;
// when it is restricted to the block, the block decides which teeth are
// enabled.
export const NoteTeethPicker: FC<Props> = ({
  block,
  teeth,
  groups,
  notation,
  multiple,
  restrictToBlock,
  teethOnly = false,
  onChange,
}) => {
  const { formatMessage } = useIntl();
  const several = multiple || !restrictToBlock;
  const offersTooth = (tooth: string) =>
    !restrictToBlock || isNoteToothOffered(block, tooth);

  const pickTooth = (tooth: string) =>
    onChange(toggle(teeth, tooth, several), several ? groups : []);
  const pickGroup = (group: NoteTeethGroup) =>
    onChange(several ? teeth : [], toggle(groups, group, several));

  const renderTooth = (tooth: string, lower: boolean) => {
    const label = formatNoteTooth(tooth, notation);
    const offered = offersTooth(tooth);
    const selected = teeth.includes(tooth);
    const covered = groups.some((group) =>
      isToothInNoteTeethGroup(tooth, group),
    );
    return (
      <button
        key={tooth}
        type="button"
        className={cn(
          styles.tooth,
          covered && styles.covered,
          selected && styles.selected,
        )}
        aria-pressed={selected}
        aria-label={formatMessage(
          {
            id: 'workspace.note.teeth.tooth',
            defaultMessage: 'Tooth {tooth}',
          },
          { tooth: label },
        )}
        disabled={!offered}
        onClick={() => pickTooth(tooth)}
      >
        <ToothView
          id={tooth}
          ISONumber={Number(tooth)}
          viewNumber={label}
          status={ToothStatus.healthy}
          isLowerJaw={lower}
          isActive={offered}
          disabled={!offered}
        />
      </button>
    );
  };

  return (
    <div className={styles.picker} data-component="workspace-note-teeth-picker">
      <TeethChart
        dentition={block.Dentition}
        offeredGroups={teethOnly ? [] : noteTeethOfferedGroups(block)}
        isGroupPressed={(group) => groups.includes(group)}
        renderTooth={renderTooth}
        onGroup={pickGroup}
      />
      {!several && (
        <p className={cn('p4', styles.hint)}>
          {teethOnly ? (
            <FormattedMessage
              id="workspace.templateEdit.teethOne"
              defaultMessage="One tooth"
            />
          ) : (
            <FormattedMessage
              id="workspace.note.teeth.single"
              defaultMessage="One tooth or group"
            />
          )}
        </p>
      )}
    </div>
  );
};
